import { type Types } from '@amplitude/analytics-browser';
import { useEffect } from 'react';

import { injectScript } from '@/inject-script.js';
import { makeQueueable } from '@/queueable.js';

declare global {
  type Amplitude = Types.BrowserClient & Types.InstanceProxy;

  interface Window {
    amplitude: Amplitude;
  }
}

const injectAmplitudeScript = () =>
  // eslint-disable-next-line sonarjs/cognitive-complexity
  new Promise<void>((res, rej) => {
    try {
      // Inject the SDK via a script tag
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, inject] = injectScript(
        'https://cdn.amplitude.com/libs/analytics-browser-1.9.1-min.js.gz',
        {
          integrity:
            'sha384-TPZhteUkZj8CAyBx+GZZytBdkuKnhKsSKcCoVCq0QSteWf/Kw5Kb9oVFUROLE1l3',
          type: 'text/javascript',
          crossOrigin: 'anonymous',
          async: true,
          onload: () => res(),
          onerror: rej,
        },
      );

      // This is a copy of the Amplitude script from https://www.docs.developers.amplitude.com/data/sdks/sdk-quickstart/#enforce-event-schemas-ampli_11,
      // but with the ability to know when the script has finished loading.
      // `inject` from above is wedged into the function and will kick off loading the script.
      // prettier-ignore
      // @ts-expect-error don't even try
      // eslint-disable-next-line
      !function(){"use strict";!function(e){var r=e.amplitude||{_q:[],_iq:[]};if(r.invoked)e.console&&console.error&&console.error("Amplitude snippet has been loaded.");else{r.invoked=!0;function v(e,t){e.prototype[t]=function(){return this._q.push({name:t,args:Array.prototype.slice.call(arguments,0)}),this}}inject();for(var o=function(){return this._q=[],this},i=["add","append","clearAll","prepend","set","setOnce","unset","preInsert","postInsert","remove","getUserProperties"],a=0;a<i.length;a++)v(o,i[a]);r.Identify=o;for(var u=function(){return this._q=[],this},c=["getEventProperties","setProductId","setQuantity","setPrice","setRevenue","setRevenueType","setEventProperties"],l=0;l<c.length;l++)v(u,c[l]);r.Revenue=u;var p=["getDeviceId","setDeviceId","getSessionId","setSessionId","getUserId","setUserId","setOptOut","setTransport","reset"],d=["init","add","remove","track","logEvent","identify","groupIdentify","setGroup","revenue","flush"];function f(e){function t(t,r){e[t]=function(){var n={promise:new Promise((r=>{e._q.push({name:t,args:Array.prototype.slice.call(arguments,0),resolve:r})}))};if(r)return n}}for(var r=0;r<p.length;r++)t(p[r],!1);for(var n=0;n<d.length;n++)t(d[n],!0)}f(r),r.createInstance=function(){var e=r._iq.push({_q:[]})-1;return f(r._iq[e]),r._iq[e]},e.amplitude=r}}(window || {})}();
    } catch (err) {
      console.error('Failed to init Amplitude', err);
    }
  });

const fireAmplitudeInitEvent = () => {
  window.dispatchEvent(new Event('amplitude_init'));
};

/**
 * Build an Amplitude tracker with strongly-typed event names and payloads.
 *
 * Usage:
 * ```
 * enum EventName {
 *   SOME_EVENT = 'Some Human Readable Event Name',
 * }
 *
 * type EventParams = {
 *   [EventName.SOME_EVENT]: {
 *     someParameter: string,
 *     optionalThing?: boolean
 *   }
 * };
 *
 * const {
 *   init,
 *   track,
 *   useTrack,
 *   getDeviceId,
 *   getDeviceIdQueryObject
 * } = buildAmplitudeTracker<EventName, EventParams>();
 *
 * track(EventName.SOME_EVENT, { someParameter: 'value' }); // OK
 * track('some other event'); // BAD
 *
 * useTrack(EventName.SOME_EVENT, { someParameter: 'value' }); // OK
 * useTrack('some other event'); // BAD
 * ```
 */
export const buildAmplitudeTracker = <
  Events extends string | number,
  Params extends { [key in Events]?: unknown },
  NeededParams = Events extends keyof Params ? Params[Events] : never,
>() => {
  const [track, flushTrackQueue] = makeQueueable(
    (
      evt: Events | `${Events}`,
      params?: NeededParams,
      opts?: Types.EventOptions,
    ) => window.amplitude.track(`${evt}`, params || undefined, opts).promise,
  );

  const useTrack = (
    evt: Events | `${Events}`,
    params?: NeededParams,
    opts?: Types.EventOptions,
  ) => {
    const paramsStr = JSON.stringify(params);
    const optsStr = JSON.stringify(opts);
    useEffect(() => {
      track(evt, params, opts);
    }, [evt, paramsStr, optsStr]);
  };

  const init = async ({
    userId,
    ...params
  }: Types.BrowserOptions & { userId?: string } = {}) => {
    if (!process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY) {
      return;
    }
    const paramsWithDeviceId = {
      ...params,
      deviceIdFromUrlParam: true,
    };

    await injectAmplitudeScript();
    await window.amplitude?.init(
      process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY,
      userId,
      paramsWithDeviceId,
    ).promise;
    fireAmplitudeInitEvent();
    flushTrackQueue();
  };

  const getDeviceId = (): string | undefined => {
    // @ts-expect-error Outdated Amplitude SDK initialized in GTM
    if (window?.amplitude?.getInstance) {
      // @ts-expect-error Outdated Amplitude SDK initialized in GTM
      const { options } = window.amplitude.getInstance();
      return options && options.deviceId;
    }
    if (window?.amplitude?.getDeviceId) {
      return window.amplitude.getDeviceId();
    }
  };

  const getDeviceIdQueryObj = (): Record<string, string | null> => {
    const deviceId = getDeviceId();
    return deviceId ? { deviceId, amp_device_id: deviceId } : {};
  };

  const flushEvents = async () => {
    flushTrackQueue();
    if (
      // @ts-expect-error Outdated Amplitude SDK initialized in GTM
      window?.amplitude?.getInstance &&
      // @ts-expect-error Outdated Amplitude SDK initialized in GTM
      window?.amplitude?.getInstance().sendEvents
    ) {
      try {
        // @ts-expect-error Outdated Amplitude SDK initialized in GTM
        window.amplitude.getInstance().sendEvents();
      } catch (err) {
        console.info('Failed to flush Amplitude events', err);
      }
    }
    if (window?.amplitude?.flush) {
      await window.amplitude.flush().promise;
    }
  };

  return {
    /**
     * Initialize Amplitude by injecting the CDN script and waiting for it to finish loading.
     * If `process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY` is not set, this is a noop and calls
     * to `track` will do nothing.
     *
     * Usage:
     * ```
     * init({
     *   appVersion: 'v1.0.1',
     * });
     * ```
     */
    init,
    /**
     * Track an event with Amplitude, queuing calls until after init is complete so nothing is lost.
     *
     * Usage:
     * ```
     * track(EventName.SOME_EVENT);
     * track(EventName.SOME_EVENT, { someParameter: 'value' });
     * await track(EventName.SOME_EVENT, { someParameter: 'value' });
     * ```
     */
    track,
    /**
     * Track everytime the parameters change.
     * Uses a string representation of params and opts to detect changes
     * so you don't have to memoize those prior to passing!
     */
    useTrack,
    /**
     * Get the amplitude device ID for anonymous tracking accross domains.
     * The result is undefined if amplitude is not initialized.
     *
     * Usage:
     * ```
     * getDeviceId();
     * ```
     */
    getDeviceId,
    /**
     * Get a query object for cross-domain tracking with both Amplitude SDKs.
     * The result is an empty query object if amplitude is not initialized.
     * @see https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/#cross-domain-tracking
     * @see https://www.docs.developers.amplitude.com/data/sdks/javascript/#cross-domain-tracking-javascript
     *
     *
     * Usage:
     * ```
     * getDeviceIdQueryObj();
     * ```
     *
     * Yields:
     * ```
     * {
     *    deviceId: 'something',
     *    amp_device_id: 'something',
     * }
     * ```
     */
    getDeviceIdQueryObj,
    /**
     * Manually flushes the event buffer and queue.
     *
     * Usage:
     * ```
     * flushEvents();
     * ```
     */
    flushEvents,
  };
};
